'use client';

import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { fetchProceduresByCategory } from '@/app/(main)/_components/search-modal/_services/search-modal-results.service';
import { useSearchSource } from '@/app/(main)/_components/search-modal/_hooks/use-search-source.hook';
import { SearchResult } from '@/app/(main)/_components/search-modal/search-modal.types';
import { useDebounceValue } from '@/hooks/use-debounce.hook';
import { useIsOnMobileDevice } from '@/hooks/use-is-on-mobile-device.hook';
import { useLoading } from '@/hooks/use-loading.hook';
import { useMostSearchedGroups } from '@/hooks/use-most-searched.hook';
import { useSearchState } from '@/store/search/search.store';
import { SearchType } from '@/types/most-search-type';
import { MostSearchedResult } from '@/types/most-search-type';
import { SelectedProcedures } from '@/app/(main)/_components/search-modal/search-modal.types';
import { isVaccineSearchMode } from '@/utils/search/search-vaccine.util';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { getLogger } from '@/logger/logger';

interface UseSelectMoreExamsProps {
  selectedProcedures: SelectedProcedures[];
  setSelectProcedures: Dispatch<SetStateAction<SelectedProcedures[]>>;
}

export function useSelectMoreExams({
  selectedProcedures,
  setSelectProcedures,
}: UseSelectMoreExamsProps) {
  const [searchDisplayValue, setSearchDisplayValue] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [emptySearchResults, setEmptySearchResults] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [showSeeSchedules, setShowSeeSchedules] = useState(true);

  const {
    isLoading: isLoadingLaboratoryProcedures,
    startLoading: startLoadingLaboratoryProcedures,
    stopLoading: stopLoadingLaboratoryProcedures,
  } = useLoading();
  const {
    isLoading: isLoadingMoreLaboratoryProcedures,
    startLoading: startLoadingMoreLaboratoryProcedures,
    stopLoading: stopLoadingMoreLaboratoryProcedures,
  } = useLoading();

  const { source, citySlug, isLoading: searchSourceIsLoading } = useSearchSource();

  const inputRef = useRef<HTMLInputElement>(null);
  const activeRequestId = useRef(0);
  const lastTrackedSearchTerm = useRef('');
  const analyticsInputValueRef = useRef('');
  const analytics = getAnalyticsPlugin();

  const inputValue = useDebounceValue(searchDisplayValue, 400);
  const analyticsInputValue = useDebounceValue(searchDisplayValue, 1000);
  const isOnMobileDevice = useIsOnMobileDevice();

  const isVaccine = isVaccineSearchMode(useSearchState.getState().selectedSearch);
  const searchType = isVaccine ? SearchType.VACCINE : SearchType.LABORATORIAL_EXAM;

  const { groups: mostSearchedGroups, isLoading: isLoadingMostSearched } = useMostSearchedGroups();

  useEffect(() => {
    analyticsInputValueRef.current = analyticsInputValue;
  }, [analyticsInputValue]);

  const fetchSearchResults = useCallback(
    async (pageNum: number, requestId: number, searchTerm: string) => {
      try {
        const { results, hasMore: moreToLoad } = await fetchProceduresByCategory({
          term: searchTerm,
          category: searchType,
          page: pageNum,
          source,
          citySlug,
        });

        if (requestId !== activeRequestId.current) {
          return;
        }

        if (pageNum === 1) {
          const isCurrentSearch = searchTerm === analyticsInputValueRef.current;
          const isNewSearchTerm = lastTrackedSearchTerm.current !== searchTerm;

          if (results.length === 0) {
            if (isCurrentSearch && isNewSearchTerm) {
              analytics.track(AnalyticsEnum.SEARCHED_PROCEDURES_MODAL_WITHOUT_RESULTS, { term: searchTerm });
              lastTrackedSearchTerm.current = searchTerm;
            }
            setEmptySearchResults(true);
            return;
          }

          if (results.length > 0 && isCurrentSearch && isNewSearchTerm) {
            analytics.track(AnalyticsEnum.SEARCHED_PROCEDURES_MODAL_WITH_RESULTS, { term: searchTerm });
            lastTrackedSearchTerm.current = searchTerm;
          }
        }

        if (pageNum > 1 && results.length === 0) {
          setHasMore(false);
          return;
        }

        setHasMore(moreToLoad);
        setSearchResults((prev) => [...prev, ...results]);
      } catch (error) {
        if (requestId === activeRequestId.current) {
          getLogger('search-modal.more-exams-fetch').error('Failed to fetch the search results', {
            search: { event: 'fetch_failed', page: pageNum },
            error,
          });

          if (pageNum === 1) {
            setSearchFailed(true);
          } else {
            setHasMore(false);
          }
        }
      }
    },
    [searchType, analytics, source, citySlug],
  );

  useEffect(() => {
    setPage(1);
    setSearchResults([]);
    setEmptySearchResults(false);
    setSearchFailed(false);
    setHasMore(false);

    if (inputValue.length === 0) {
      lastTrackedSearchTerm.current = '';
      return;
    }

    if (inputValue.length <= 2) {
      return;
    }

    if (searchSourceIsLoading) {
      return;
    }

    const currentRequestId = ++activeRequestId.current;
    startLoadingLaboratoryProcedures();

    fetchSearchResults(1, currentRequestId, inputValue).finally(() => {
      if (currentRequestId === activeRequestId.current) {
        stopLoadingLaboratoryProcedures();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, searchType, searchSourceIsLoading, retryCount]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) {
      return;
    }

    const onFocus = () => {
      if (isOnMobileDevice) {
        setShowSeeSchedules(false);
      }
    };
    const onBlur = () => {
      if (isOnMobileDevice) {
        setShowSeeSchedules(true);
      }
    };

    el.addEventListener('focus', onFocus);
    el.addEventListener('blur', onBlur);
    return () => {
      el.removeEventListener('focus', onFocus);
      el.removeEventListener('blur', onBlur);
    };
  }, [isOnMobileDevice]);

  const handleSelectProcedure = useCallback(
    (procedure: MostSearchedResult | SearchResult | SelectedProcedures) => {
      analytics.track(AnalyticsEnum.VIEW_SEARCH_SELECT_MORE_EXAMES);
      if (selectedProcedures.some((item) => item.id === procedure.id)) {
        setSelectProcedures((prev) => prev.filter((item) => item.id !== procedure.id));
        return;
      }

      setSelectProcedures((prev) => {
        if (prev.some((item) => item.id === procedure.id)) {
          return prev;
        }
        return [
          ...prev,
          {
            id: procedure.id || '',
            code: procedure.code || '',
            displayName: procedure.displayName || '',
          },
        ];
      });
    },
    [selectedProcedures, setSelectProcedures, analytics],
  );

  const isChecked = useCallback(
    (procedure: MostSearchedResult | SearchResult | SelectedProcedures) =>
      selectedProcedures.some((item) => item.id === procedure.id),
    [selectedProcedures],
  );

  const handleRetrySearch = () => {
    setRetryCount((count) => count + 1);
  };

  const handleSearchInput = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchDisplayValue(e.target.value);
  };

  const handleLoadMore = useCallback(
    async () => {
      const newPage = page + 1;
      const currentRequestId = activeRequestId.current;

      setPage(newPage);
      startLoadingMoreLaboratoryProcedures();

      await fetchSearchResults(newPage, currentRequestId, inputValue).finally(() => {
        if (currentRequestId === activeRequestId.current) {
          stopLoadingMoreLaboratoryProcedures();
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, inputValue, fetchSearchResults],
  );

  const hasSearchResults = searchResults.length > 0;
  const isSearching = inputValue.length > 0;

  const mostSearchedProcedures
    = mostSearchedGroups.find((group) => group.type === searchType)?.procedures ?? [];
  const isLoadingProcedures
    = isLoadingLaboratoryProcedures || (!hasSearchResults && isLoadingMostSearched);

  return {
    searchDisplayValue,
    handleSearchInput,
    inputRef,
    searchResults,
    mostSearchedProcedures,
    emptySearchResults,
    searchFailed,
    handleRetrySearch,
    isLoadingProcedures,
    isLoadingMoreLaboratoryProcedures,
    handleSelectProcedure,
    isChecked,
    handleLoadMore,
    hasSearchResults,
    isSearching,
    hasMore,
    showSeeSchedules,
    searchInputPlaceholder: isVaccine ? 'Busque pela vacina' : 'Busque pelo exame',
    addMoreExamsMessage: isVaccine ? 'Adicione mais vacinas.' : 'Adicione mais exames laboratoriais.',
    selectedExamsCount:
      selectedProcedures.length === 1
        ? `Ver ${isVaccine ? 'selecionada' : 'selecionado'} (1)`
        : `Ver ${isVaccine ? 'selecionadas' : 'selecionados'} (${selectedProcedures.length})`,
    searchType,
  };
}
